import { useCallback } from 'react';

import { ticketUpdate } from '~/actions/ticket';
import { Uuid } from '~/eds/src/types/Uuid';
import { TicketNegotiationStatus } from '~/enums/TicketNegotiationStatus';
import { api } from '~/redux/api/createApi';
import type { SupplierContactPayload } from '~/redux/api/methods/wss/postTicketSupplierNegotiation';
import { useAppDispatch } from '~/rootState';

export function useTicketSupplierNegotiation() {
  const dispatch = useAppDispatch();
  const [mutate, { isLoading }] =
    api.endpoints.postTicketSupplierNegotiation.useMutation();

  const postShare = useCallback(
    async (params: {
      ticketId: Uuid;
      versionId: string;
      contractId: string;
      supplierId: string;
      contacts: SupplierContactPayload[];
    }) => {
      const result = await mutate({
        ticketId: params.ticketId,
        payload: {
          action: 'share',
          version_id: params.versionId,
          contract_id: params.contractId,
          supplier_company_wss_id: String(params.supplierId),
          supplier_contacts: params.contacts,
        },
      }).unwrap();
      // update the negotiation status in the store, so the UI is updated immediately
      dispatch(
        ticketUpdate({ negotiationStatus: TicketNegotiationStatus.InProgress }),
      );
      return result;
    },
    [mutate, dispatch],
  );

  const postRetract = useCallback(
    async (params: {
      ticketId: Uuid;
      versionId: string;
      contractId: string;
    }) => {
      const result = await mutate({
        ticketId: params.ticketId,
        payload: {
          action: 'retract',
          version_id: params.versionId,
          contract_id: params.contractId,
        },
      }).unwrap();
      // update the negotiation status in the store, so the UI is updated immediately
      dispatch(
        ticketUpdate({ negotiationStatus: TicketNegotiationStatus.Retracted }),
      );
      return result;
    },
    [mutate, dispatch],
  );

  return { postShare, postRetract, isLoading };
}
